import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useKeystrokeCapture } from '@/hooks/useKeystrokeCapture';
import type { BiometricFeatures, KeystrokeEvent } from '@/types/keystroke';

interface TypingAreaProps {
  /** The prompt text the user should type */
  promptText: string;
  /** Called when the user has finished typing the full text */
  onComplete?: (events: KeystrokeEvent[], features: BiometricFeatures, typedText: string) => void;
  /** Called on each keystroke for live metrics */
  onKeystroke?: (keystrokeCount: number, waveformData: number[]) => void;
  /** Whether the area is interactive */
  disabled?: boolean;
  /** Placeholder text shown before typing starts */
  placeholder?: string;
  /** Show live WPM */
  showLiveMetrics?: boolean;
  /** Auto-focus on mount */
  autoFocus?: boolean;
  /** Minimum completion percentage (0-1) before allowing submit */
  minCompletion?: number;
}

interface CharState {
  char: string;
  status: 'pending' | 'correct' | 'incorrect' | 'extra';
}

export default function TypingArea({
  promptText,
  onComplete,
  onKeystroke,
  disabled = false,
  placeholder = 'Clique aqui e comece a digitar...',
  showLiveMetrics = true,
  autoFocus = true,
  minCompletion = 0.9,
}: TypingAreaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [typedChars, setTypedChars] = useState<string[]>([]);
  const [charStates, setCharStates] = useState<CharState[]>([]);
  const [cursorPos, setCursorPos] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [shakeIndex, setShakeIndex] = useState<number | null>(null);
  const [liveWpm, setLiveWpm] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  const {
    captureRef,
    state: captureState,
    events,
    features,
    waveformData,
    keystrokeCount,
    start,
    stop,
    extractFeatures,
  } = useKeystrokeCapture({ autoStart: false, extractDebounce: 500 });

  // Initialize char states from prompt text
  useEffect(() => {
    setCharStates(
      promptText.split('').map((char) => ({ char, status: 'pending' })),
    );
    setTypedChars([]);
    setCursorPos(0);
    setIsComplete(false);
    startTimeRef.current = null;
  }, [promptText]);

  // Notify parent of keystroke updates
  useEffect(() => {
    onKeystroke?.(keystrokeCount, waveformData);
  }, [keystrokeCount, waveformData, onKeystroke]);

  // Compute live WPM
  useEffect(() => {
    if (!startTimeRef.current || typedChars.length === 0) {
      setLiveWpm(0);
      return;
    }
    const elapsed = (performance.now() - startTimeRef.current) / 60000;
    if (elapsed > 0) {
      setLiveWpm(Math.round((typedChars.length / 5) / elapsed));
    }
  }, [typedChars]);

  // Setup the capture on the hidden input-like area
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (el) {
        captureRef(el);
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [captureRef],
  );

  // Handle keyboard input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || isComplete) return;

      // Start capturing on first key
      if (captureState === 'idle') {
        start();
        startTimeRef.current = performance.now();
      }

      const key = e.key;

      if (key === 'Backspace') {
        e.preventDefault();
        if (cursorPos > 0) {
          setTypedChars((prev) => prev.slice(0, -1));
          setCursorPos((prev) => prev - 1);
          setCharStates((prev) => {
            const next = [...prev];
            const idx = cursorPos - 1;
            if (idx < promptText.length) {
              next[idx] = { ...next[idx], status: 'pending' };
            }
            // Remove extra chars
            if (cursorPos > promptText.length) {
              next.pop();
            }
            return next;
          });
        }
        return;
      }

      // Ignore modifier-only keys, escape, tabs, etc.
      if (key.length !== 1 && key !== 'Enter') return;

      e.preventDefault();

      const charToType = key === 'Enter' ? '\n' : key;
      const pos = cursorPos;

      setTypedChars((prev) => [...prev, charToType]);
      setCursorPos((prev) => prev + 1);

      setCharStates((prev) => {
        const next = [...prev];
        if (pos < promptText.length) {
          const isCorrect = charToType === promptText[pos];
          next[pos] = { ...next[pos], status: isCorrect ? 'correct' : 'incorrect' };

          if (!isCorrect) {
            setShakeIndex(pos);
            setTimeout(() => setShakeIndex(null), 300);
          }
        } else {
          // Extra characters beyond the prompt
          next.push({ char: charToType, status: 'extra' });
        }
        return next;
      });

      // Check completion
      const newPos = pos + 1;
      if (newPos >= promptText.length) {
        const completionRatio = newPos / promptText.length;
        if (completionRatio >= minCompletion) {
          handleComplete();
        }
      }
    },
    [disabled, isComplete, cursorPos, captureState, promptText, start, minCompletion],
  );

  const handleComplete = useCallback(async () => {
    stop();
    setIsComplete(true);

    const extracted = await extractFeatures();
    if (extracted && onComplete) {
      onComplete(events, extracted, typedChars.join(''));
    }
  }, [stop, extractFeatures, events, typedChars, onComplete]);

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  // Compute statistics for display
  const correctCount = charStates.filter((c) => c.status === 'correct').length;
  const incorrectCount = charStates.filter((c) => c.status === 'incorrect').length;
  const accuracy = correctCount + incorrectCount > 0
    ? (correctCount / (correctCount + incorrectCount)) * 100
    : 100;

  return (
    <div className="w-full max-w-typing mx-auto">
      {/* Typing container */}
      <div
        ref={setRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`
          relative min-h-[200px] p-6 rounded-card
          bg-bg-secondary border-2 transition-colors duration-200
          font-mono text-lg leading-relaxed tracking-wide
          outline-none cursor-text select-none
          ${isFocused ? 'border-accent-brand' : 'border-bg-surface'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${isComplete ? 'border-accent-success' : ''}
        `}
        role="textbox"
        aria-label="Area de digitacao biometrica"
        autoFocus={autoFocus}
      >
        {/* Prompt characters */}
        <div className="flex flex-wrap">
          {charStates.map((cs, index) => (
            <span
              key={index}
              className={`
                relative inline-block transition-colors duration-100
                ${cs.status === 'pending' ? 'text-txt-muted' : ''}
                ${cs.status === 'correct' ? 'text-txt-typed' : ''}
                ${cs.status === 'incorrect' ? 'text-accent-danger bg-accent-danger/20 rounded' : ''}
                ${cs.status === 'extra' ? 'text-accent-danger/70' : ''}
                ${shakeIndex === index ? 'animate-shake' : ''}
                ${cs.char === ' ' ? 'w-[0.5ch]' : ''}
              `}
            >
              {/* Cursor */}
              {index === cursorPos && isFocused && !isComplete && (
                <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-accent-brand animate-cursor-blink" />
              )}
              {cs.char === ' ' ? '\u00A0' : cs.char}
            </span>
          ))}
          {/* Cursor at end */}
          {cursorPos >= charStates.length && isFocused && !isComplete && (
            <span className="inline-block w-[2px] h-[1.2em] bg-accent-brand animate-cursor-blink align-middle" />
          )}
        </div>

        {/* Placeholder */}
        {cursorPos === 0 && !isFocused && (
          <div className="absolute inset-0 flex items-center justify-center text-txt-muted text-base pointer-events-none">
            {placeholder}
          </div>
        )}

        {/* Focus indicator */}
        {!isFocused && cursorPos === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="px-4 py-2 bg-bg-surface/80 rounded-lg text-txt-muted text-sm">
              Clique para focar
            </div>
          </div>
        )}
      </div>

      {/* Live metrics bar */}
      {showLiveMetrics && cursorPos > 0 && (
        <div className="flex items-center justify-between mt-3 px-2 text-sm text-txt-muted font-sans">
          <div className="flex items-center gap-4">
            <span>
              <span className="text-accent-info font-medium">{liveWpm}</span> WPM
            </span>
            <span>
              <span className={`font-medium ${accuracy >= 95 ? 'text-accent-success' : accuracy >= 85 ? 'text-accent-warning' : 'text-accent-danger'}`}>
                {accuracy.toFixed(0)}%
              </span>{' '}
              precisao
            </span>
            <span>
              <span className="text-txt-primary font-medium">{cursorPos}</span>/{promptText.length} chars
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && (
              <span className="text-accent-success font-medium">Completo</span>
            )}
            {captureState === 'capturing' && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-accent-danger animate-pulse-dot" />
                Capturando
              </span>
            )}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="mt-2 h-1 bg-bg-surface rounded-full overflow-hidden">
        <div
          className="h-full bg-accent-brand transition-all duration-150 ease-out rounded-full"
          style={{ width: `${Math.min((cursorPos / promptText.length) * 100, 100)}%` }}
        />
      </div>
    </div>
  );
}
